import React, { useMemo } from 'react';
import { Paper, Typography, Box } from '@mui/material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Visit } from '../types';
import { 
  CustomEvent, 
  // TimeSlotWrapper
} from '@/components/Calendar';
// import { TimeSlot } from '@/components/Calendar/TimeSlotWrapper';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    'en-US': enUS
  }
});

interface VisitCalendarProps {
  visits: Visit[];
  onVisitSelect: (visit: Visit) => void;
}

export const VisitCalendar: React.FC<VisitCalendarProps> = ({
  visits,
  onVisitSelect
}) => {
  const events = useMemo(() => visits.map(visit => ({
    id: visit.id,
    title: `${visit.visitType.charAt(0).toUpperCase() + visit.visitType.slice(1)} Visit - Student ${visit.studentId}`,
    start: new Date(`${visit.date}`),
    end: new Date(`${visit.date.split('T')[0]}T${visit.endTime}`),
    resource: visit
  })), [visits]);

  const formats = useMemo(() => ({
    timeGutterFormat: (date: Date) => format(date, 'h a'),
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => (
      `${format(start, 'h:mm')} - ${format(end, 'h:mm a')}`
    ),
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) => (
      `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
    )
  }), []);

  return (
    <Paper sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="h2">
          Visit Schedule
        </Typography>
      </Box>

      <Box sx={{
        flexGrow: 1,
        p: 2,
        minHeight: 0,
        '.rbc-calendar': {
          height: '100%',
          fontFamily: 'inherit'
        },
        '.rbc-header': {
          padding: '8px',
          fontWeight: 500,
          fontSize: '0.875rem'
        },
        '.rbc-label': {
          padding: '0 8px',
          fontSize: '0.75rem'
        },
        '.rbc-timeslot-group': {
          minHeight: '60px',
          '.rbc-time-slot': {
            height: '30px'
          }
        },
        '.rbc-time-slot': {
          fontSize: '0.75rem'
        },
        '.rbc-event': {
          backgroundColor: 'primary.main',
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: 'primary.dark'
          }
        },
        '.rbc-today': {
          backgroundColor: 'primary.lighter'
        },
        '.rbc-current-time-indicator': {
          backgroundColor: 'primary.main'
        }
      }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="week"
          views={['week', 'day']}
          min={new Date(2023, 0, 1, 8, 0)}
          max={new Date(2023, 0, 1, 18, 0)}
          onSelectEvent={(event: any) => onVisitSelect(event.resource)}
          formats={formats}
          step={30}
          timeslots={2}
          components={{
            event: CustomEvent,
            // timeSlotWrapper: TimeSlot,
            // dayColumn: undefined
          }}
        />
      </Box>
    </Paper>
  );
};
